/**
 * AuthModals — Login, Register, Forgot Password modal dialogs
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

import { Navigation } from '/js/ui/Navigation.js';
import { PreferencesService } from '/js/ui/PreferencesService.js';
import { t } from '/js/i18n.js';

/** @type {Function} */
let notifyFn = () => {};

/**
 * Initialize auth modals with a notification callback
 * @param {Function} showNotification - (text, type) => void
 */
export function initAuthModals(showNotification) {
    notifyFn = showNotification;

    // Listen for Navigation.js button clicks
    document.addEventListener('nav:login', () => showLoginModal());
    document.addEventListener('nav:register', () => showRegisterModal());
}

// =========================================================================
// VALIDATION HELPERS
// =========================================================================

/**
 * Show field-level error below an input
 * @param {HTMLElement} group - .form-group container
 * @param {HTMLInputElement} input - the input element
 * @param {string} message - error text
 */
function _showFieldError(group, input, message) {
    input.classList.add('field-error');
    // Remove existing error text if any
    const existing = group.querySelector('.field-error-text');
    if (existing) existing.remove();

    const errorEl = document.createElement('span');
    errorEl.className = 'field-error-text';
    errorEl.textContent = message;
    errorEl.setAttribute('role', 'alert');
    group.appendChild(errorEl);
}

/**
 * Clear field-level error
 * @param {HTMLElement} group - .form-group container
 * @param {HTMLInputElement} input - the input element
 */
function _clearFieldError(group, input) {
    input.classList.remove('field-error');
    const existing = group.querySelector('.field-error-text');
    if (existing) existing.remove();
}

/**
 * Shake the modal card on error
 * @param {HTMLElement} card - .modal-card element
 */
function _shakeModal(card) {
    card.classList.remove('modal-shake');
    // Force reflow to restart animation
    void card.offsetWidth;
    card.classList.add('modal-shake');
    card.addEventListener('animationend', () => {
        card.classList.remove('modal-shake');
    }, { once: true });
}

/**
 * Show success checkmark overlay, then resolve after delay
 * @param {HTMLElement} card - .modal-card element
 * @returns {Promise<void>}
 */
function _showSuccessAnimation(card) {
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'auth-success-overlay';
        const check = document.createElement('span');
        check.className = 'auth-success-check';
        check.textContent = '\u2714';
        overlay.appendChild(check);
        card.appendChild(overlay);
        setTimeout(resolve, 500);
    });
}

/**
 * Set button to loading state with spinner
 * @param {HTMLButtonElement} btn
 * @param {string} loadingText
 */
function _setButtonLoading(btn, loadingText) {
    btn.disabled = true;
    btn.textContent = '';
    const spinner = document.createElement('span');
    spinner.className = 'btn-spinner';
    btn.appendChild(spinner);
    btn.appendChild(document.createTextNode(loadingText));
}

/**
 * Reset button from loading state
 * @param {HTMLButtonElement} btn
 * @param {string} text
 */
function _resetButton(btn, text) {
    btn.disabled = false;
    btn.textContent = text;
}

/**
 * Update submit button disabled state based on required fields
 * @param {HTMLButtonElement} btn
 * @param {HTMLInputElement[]} fields
 */
function _updateSubmitState(btn, fields) {
    const allFilled = fields.every((f) => f.value.trim().length > 0);
    btn.classList.toggle('btn-submit-disabled', !allFilled);
}

/**
 * Attach blur validation to a field
 * @param {HTMLElement} group
 * @param {HTMLInputElement} input
 * @param {Function} validate - returns error string or null
 */
function _attachBlurValidation(group, input, validate) {
    input.addEventListener('blur', () => {
        if (input.value.trim().length === 0) return; // Don't validate empty on blur
        const error = validate(input.value);
        if (error) {
            _showFieldError(group, input, error);
        } else {
            _clearFieldError(group, input);
        }
    });
    input.addEventListener('input', () => {
        _clearFieldError(group, input);
    });
}

// =========================================================================
// LOGIN MODAL
// =========================================================================

/**
 * Show login modal
 */
export function showLoginModal() {
    const content = document.createElement('div');
    content.className = 'auth-modal-content';

    const form = document.createElement('div');
    form.className = 'auth-form';

    // Email/Username field
    const emailGroup = document.createElement('div');
    emailGroup.className = 'form-group';
    const emailLabel = document.createElement('label');
    emailLabel.setAttribute('for', 'login-email');
    emailLabel.textContent = t('auth.emailOrUsername');
    const emailInput = document.createElement('input');
    emailInput.type = 'text';
    emailInput.id = 'login-email';
    emailInput.className = 'form-control';
    emailInput.placeholder = t('auth.emailOrUsername');
    emailInput.autocomplete = 'username';
    emailGroup.appendChild(emailLabel);
    emailGroup.appendChild(emailInput);
    form.appendChild(emailGroup);

    // Password field
    const pwGroup = document.createElement('div');
    pwGroup.className = 'form-group';
    const pwLabel = document.createElement('label');
    pwLabel.setAttribute('for', 'login-password');
    pwLabel.textContent = t('auth.password');
    const pwInput = document.createElement('input');
    pwInput.type = 'password';
    pwInput.id = 'login-password';
    pwInput.className = 'form-control';
    pwInput.placeholder = t('auth.password');
    pwInput.autocomplete = 'current-password';
    pwGroup.appendChild(pwLabel);
    pwGroup.appendChild(pwInput);
    form.appendChild(pwGroup);

    // Forgot link
    const footer = document.createElement('div');
    footer.className = 'auth-form-footer';
    const forgotLink = document.createElement('button');
    forgotLink.className = 'auth-link-small';
    forgotLink.id = 'login-forgot-link';
    forgotLink.textContent = t('auth.forgotPassword');
    footer.appendChild(forgotLink);
    form.appendChild(footer);

    content.appendChild(form);

    // Modal wrapper
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';

    const card = document.createElement('div');
    card.className = 'modal-card';

    const title = document.createElement('h3');
    title.className = 'modal-title';
    title.textContent = t('auth.login');

    const btnRow = document.createElement('div');
    btnRow.className = 'modal-buttons';

    const btnLogin = document.createElement('button');
    btnLogin.className = 'btn btn-primary btn-submit-disabled';
    btnLogin.textContent = t('auth.login');

    const btnCancel = document.createElement('button');
    btnCancel.className = 'btn btn-ghost';
    btnCancel.textContent = t('common.cancel');

    btnRow.appendChild(btnCancel);
    btnRow.appendChild(btnLogin);
    card.appendChild(title);
    card.appendChild(content);
    card.appendChild(btnRow);
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    setTimeout(() => emailInput.focus(), 100);

    // Track input for submit-disabled state
    const requiredFields = [emailInput, pwInput];
    for (const field of requiredFields) {
        field.addEventListener('input', () => _updateSubmitState(btnLogin, requiredFields));
    }

    async function doLogin() {
        const identifier = emailInput.value.trim();
        const password = pwInput.value;

        // Field-level empty validation
        let hasError = false;
        if (!identifier) {
            _showFieldError(emailGroup, emailInput, t('auth.fieldRequired'));
            hasError = true;
        }
        if (!password) {
            _showFieldError(pwGroup, pwInput, t('auth.fieldRequired'));
            hasError = true;
        }
        if (hasError) {
            _shakeModal(card);
            return;
        }

        _setButtonLoading(btnLogin, t('auth.loggingIn'));

        try {
            const res = await fetch('/api/v1/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ identifier, password })
            });
            const data = await res.json();

            if (res.ok && data.totp_required) {
                // 2FA required — show TOTP input
                _show2FAPrompt(overlay, data.challengeToken, notifyFn);
            } else if (res.ok && data.accessToken) {
                await _showSuccessAnimation(card);
                overlay.remove();
                localStorage.setItem('accessToken', data.accessToken);
                Navigation.setUser(data.user);
                // Sync preferences from server (fire-and-forget)
                PreferencesService.syncFromServer().catch(() => {});
                notifyFn(t('auth.loginWelcome', { name: data.user.username }), 'success');
            } else {
                _shakeModal(card);
                notifyFn(data.message || data.error || t('auth.loginError'), 'error');
                _resetButton(btnLogin, t('auth.login'));
            }
        } catch (err) {
            console.warn('[AuthModals] Login request failed:', err);
            _shakeModal(card);
            notifyFn(t('auth.connectionError'), 'error');
            _resetButton(btnLogin, t('auth.login'));
        }
    }

    btnLogin.addEventListener('click', doLogin);
    pwInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') doLogin();
    });
    btnCancel.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) overlay.remove();
    });
    forgotLink.addEventListener('click', () => {
        overlay.remove();
        showForgotPasswordModal();
    });
}

// =========================================================================
// REGISTER MODAL
// =========================================================================

/**
 * Show register modal
 */
export function showRegisterModal() {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';

    const card = document.createElement('div');
    card.className = 'modal-card';

    const title = document.createElement('h3');
    title.className = 'modal-title';
    title.textContent = t('auth.register');
    card.appendChild(title);

    const form = document.createElement('div');
    form.className = 'auth-form';

    // Username
    const userGroup = _createField('reg-username', t('auth.nickname'), 'text', t('auth.usernameHint'), 'username');
    form.appendChild(userGroup);

    // Email
    const emailGroup = _createField('reg-email', t('auth.email'), 'email', t('auth.email'), 'email');
    form.appendChild(emailGroup);

    // Password
    const pwGroup = _createField(
        'reg-password',
        t('auth.password'),
        'password',
        t('auth.passwordHint'),
        'new-password'
    );
    const strengthEl = document.createElement('div');
    strengthEl.className = 'password-strength';
    strengthEl.id = 'reg-strength';
    pwGroup.appendChild(strengthEl);
    form.appendChild(pwGroup);

    // Buttons
    const btnRow = document.createElement('div');
    btnRow.className = 'modal-buttons';
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn btn-ghost';
    cancelBtn.textContent = t('common.cancel');
    const submitBtn = document.createElement('button');
    submitBtn.className = 'btn btn-primary btn-submit-disabled';
    submitBtn.id = 'reg-submit';
    submitBtn.textContent = t('auth.register');
    btnRow.appendChild(cancelBtn);
    btnRow.appendChild(submitBtn);
    form.appendChild(btnRow);

    card.appendChild(form);
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    const usernameInput = document.getElementById('reg-username');
    const emailInput = document.getElementById('reg-email');
    const passwordInput = document.getElementById('reg-password');

    setTimeout(() => usernameInput?.focus(), 100);

    // Track input for submit-disabled state
    const requiredFields = [usernameInput, emailInput, passwordInput];
    for (const field of requiredFields) {
        field.addEventListener('input', () => _updateSubmitState(submitBtn, requiredFields));
    }

    // Blur validations
    _attachBlurValidation(userGroup, usernameInput, (val) => {
        if (val.trim().length < 3) return t('auth.usernameTooShort');
        return null;
    });

    _attachBlurValidation(pwGroup, passwordInput, (val) => {
        if (val.length < 8) return t('auth.passwordTooShort');
        return null;
    });

    // Password strength indicator
    passwordInput?.addEventListener('input', (e) => {
        const pw = e.target.value;
        let strength = 0;
        if (pw.length >= 8) strength++;
        if (pw.length >= 12) strength++;
        if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) strength++;
        if (/\d/.test(pw)) strength++;
        if (/[^A-Za-z0-9]/.test(pw)) strength++;

        const labels = [
            '',
            t('auth.passwordStrength.weak'),
            t('auth.passwordStrength.ok'),
            t('auth.passwordStrength.good'),
            t('auth.passwordStrength.strong'),
            t('auth.passwordStrength.veryStrong')
        ];
        const colors = ['', '#ff4444', '#ff8800', '#ffcc00', '#88ff00', '#00ff88'];
        strengthEl.textContent = labels[strength] || '';
        strengthEl.style.color = colors[strength] || '';
    });

    submitBtn.addEventListener('click', async () => {
        const username = usernameInput.value.trim();
        const email = emailInput.value.trim();
        const password = passwordInput.value;

        // Field-level validation
        let hasError = false;
        if (!username) {
            _showFieldError(userGroup, usernameInput, t('auth.fieldRequired'));
            hasError = true;
        } else if (username.length < 3) {
            _showFieldError(userGroup, usernameInput, t('auth.usernameTooShort'));
            hasError = true;
        }
        if (!email) {
            _showFieldError(emailGroup, emailInput, t('auth.fieldRequired'));
            hasError = true;
        }
        if (!password) {
            _showFieldError(pwGroup, passwordInput, t('auth.fieldRequired'));
            hasError = true;
        } else if (password.length < 8) {
            _showFieldError(pwGroup, passwordInput, t('auth.passwordTooShort'));
            hasError = true;
        }
        if (hasError) {
            _shakeModal(card);
            return;
        }

        _setButtonLoading(submitBtn, t('auth.registering'));

        try {
            const res = await fetch('/api/v1/auth/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, email, password })
            });
            const data = await res.json();

            if (res.ok) {
                await _showSuccessAnimation(card);
                overlay.remove();
                notifyFn(t('auth.registerSuccess'), 'success');
                const loginRes = await fetch('/api/v1/auth/login', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ identifier: email, password })
                });
                const loginData = await loginRes.json();
                if (loginRes.ok && loginData.user) {
                    if (loginData.accessToken) localStorage.setItem('accessToken', loginData.accessToken);
                    Navigation.setUser(loginData.user);
                    // First registration: upload all local prefs to server (fire-and-forget)
                    PreferencesService.syncToServer().catch(() => {});
                }
            } else {
                _shakeModal(card);
                notifyFn(data.message || data.error || t('auth.registerError'), 'error');
                _resetButton(submitBtn, t('auth.register'));
            }
        } catch (err) {
            console.warn('[AuthModals] Register request failed:', err);
            _shakeModal(card);
            notifyFn(t('auth.connectionError'), 'error');
            _resetButton(submitBtn, t('auth.register'));
        }
    });

    cancelBtn.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) overlay.remove();
    });
}

// =========================================================================
// FORGOT PASSWORD MODAL
// =========================================================================

/**
 * Show forgot password modal
 */
export function showForgotPasswordModal() {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';

    const card = document.createElement('div');
    card.className = 'modal-card';

    const title = document.createElement('h3');
    title.className = 'modal-title';
    title.textContent = t('auth.forgotPasswordTitle');
    card.appendChild(title);

    const form = document.createElement('div');
    form.className = 'auth-form';

    const hint = document.createElement('p');
    hint.className = 'text-muted';
    hint.style.marginBottom = '12px';
    hint.textContent = t('auth.forgotPasswordHint');
    form.appendChild(hint);

    const emailGroup = _createField('forgot-email', '', 'email', t('auth.email'), 'email');
    form.appendChild(emailGroup);

    const btnRow = document.createElement('div');
    btnRow.className = 'modal-buttons';
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn btn-ghost';
    cancelBtn.textContent = t('common.cancel');
    const submitBtn = document.createElement('button');
    submitBtn.className = 'btn btn-primary btn-submit-disabled';
    submitBtn.textContent = t('auth.forgotPasswordSendLink');
    btnRow.appendChild(cancelBtn);
    btnRow.appendChild(submitBtn);
    form.appendChild(btnRow);

    card.appendChild(form);
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    const emailInput = document.getElementById('forgot-email');
    setTimeout(() => emailInput?.focus(), 100);

    // Track input for submit-disabled state
    emailInput.addEventListener('input', () => _updateSubmitState(submitBtn, [emailInput]));

    submitBtn.addEventListener('click', async () => {
        const email = emailInput.value.trim();
        if (!email) {
            _showFieldError(emailGroup, emailInput, t('auth.fieldRequired'));
            _shakeModal(card);
            return;
        }

        _setButtonLoading(submitBtn, t('auth.sending'));

        try {
            await fetch('/api/v1/auth/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            await _showSuccessAnimation(card);
            overlay.remove();
            notifyFn(t('auth.forgotPasswordSent'), 'success');
        } catch (err) {
            console.warn('[AuthModals] Forgot-password request failed:', err);
            _shakeModal(card);
            notifyFn(t('auth.forgotPasswordSendError'), 'error');
            _resetButton(submitBtn, t('auth.forgotPasswordSendLink'));
        }
    });

    cancelBtn.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) overlay.remove();
    });
}

// =========================================================================
// 2FA PROMPT
// =========================================================================

/**
 * Show 2FA TOTP input after successful password auth
 */
function _show2FAPrompt(overlay, challengeToken, notify) {
    // Replace modal content with TOTP input
    const card = overlay.querySelector('.modal-card');
    if (!card) return;

    // Clear existing children safely
    while (card.firstChild) card.removeChild(card.firstChild);

    const title = document.createElement('h3');
    title.className = 'modal-title';
    title.textContent = t('auth.2fa_title');
    card.appendChild(title);

    const desc = document.createElement('p');
    desc.className = 'auth-2fa-desc';
    desc.textContent = t('auth.2fa_description');
    card.appendChild(desc);

    const codeInput = document.createElement('input');
    codeInput.type = 'text';
    codeInput.className = 'form-control';
    codeInput.id = 'totp-code';
    codeInput.placeholder = '000000';
    codeInput.maxLength = 6;
    codeInput.pattern = '[0-9]{6}';
    codeInput.autocomplete = 'one-time-code';
    codeInput.inputMode = 'numeric';
    codeInput.style.textAlign = 'center';
    codeInput.style.fontSize = '1.5rem';
    codeInput.style.letterSpacing = '0.3rem';
    card.appendChild(codeInput);

    const btnRow = document.createElement('div');
    btnRow.className = 'modal-buttons';
    btnRow.style.marginTop = '1rem';

    const btnCancel = document.createElement('button');
    btnCancel.className = 'btn btn-ghost';
    btnCancel.textContent = t('common.cancel');
    btnCancel.addEventListener('click', () => overlay.remove());

    const btnVerify = document.createElement('button');
    btnVerify.className = 'btn btn-primary btn-submit-disabled';
    btnVerify.textContent = t('auth.2fa_verify');

    // Track input for submit-disabled state
    codeInput.addEventListener('input', () => {
        const valid = /^\d{6}$/.test(codeInput.value.trim());
        btnVerify.classList.toggle('btn-submit-disabled', !valid);
    });

    btnRow.appendChild(btnCancel);
    btnRow.appendChild(btnVerify);
    card.appendChild(btnRow);

    setTimeout(() => codeInput.focus(), 100);

    async function verify() {
        const code = codeInput.value.trim();
        if (code.length !== 6 || !/^\d{6}$/.test(code)) {
            notify(t('auth.2fa_invalid'), 'error');
            _shakeModal(card);
            return;
        }

        _setButtonLoading(btnVerify, t('auth.verifying'));

        try {
            const res = await fetch('/api/v1/auth/verify-2fa', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ challengeToken, code })
            });
            const data = await res.json();

            if (res.ok && data.accessToken) {
                await _showSuccessAnimation(card);
                overlay.remove();
                localStorage.setItem('accessToken', data.accessToken);
                Navigation.setUser(data.user);
                // Sync preferences from server after 2FA login (fire-and-forget)
                PreferencesService.syncFromServer().catch(() => {});
                notify(t('auth.loginWelcome', { name: data.user.username }), 'success');
            } else {
                _shakeModal(card);
                notify(data.message || t('auth.2fa_invalid'), 'error');
                _resetButton(btnVerify, t('auth.2fa_verify'));
                codeInput.value = '';
                codeInput.focus();
            }
        } catch (err) {
            console.warn('[AuthModals] 2FA verify request failed:', err);
            _shakeModal(card);
            notify(t('auth.connectionError'), 'error');
            _resetButton(btnVerify, t('auth.2fa_verify'));
        }
    }

    btnVerify.addEventListener('click', verify);
    codeInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') verify();
    });
}

// =========================================================================
// HELPERS
// =========================================================================

// Helper to create a form field group
function _createField(id, label, type, placeholder, autocomplete) {
    const group = document.createElement('div');
    group.className = 'form-group';
    if (label) {
        const labelEl = document.createElement('label');
        labelEl.setAttribute('for', id);
        labelEl.textContent = label;
        group.appendChild(labelEl);
    }
    const input = document.createElement('input');
    input.type = type;
    input.id = id;
    input.className = 'form-control';
    if (placeholder) input.placeholder = placeholder;
    if (autocomplete) input.autocomplete = autocomplete;
    group.appendChild(input);
    return group;
}
